import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { coursesApi, certificateExamApi, enrollmentApi } from "../lib";
import { showSuccess, showError } from "../lib/toast";
import {
  ArrowLeft,
  ArrowRight,
  Trophy,
  Award,
  Clock,
  CheckCircle,
  XCircle,
  Home,
  Target,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Zap,
  Star,
} from "lucide-react";

interface ExamQuestion {
  id: string;
  questionText: string;
  options: Array<{ id: string; optionText: string }>;
  points: number;
}

interface ExamData {
  _id: string;
  title: string;
  description?: string;
  passingScore: number;
  timeLimit: number;
  questions: ExamQuestion[];
  totalPoints: number;
  examDuration: number;
  attemptsAllowed: number;
}

export default function CertificateExamPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [course, setCourse] = useState<{ title: string } | null>(null);
  const [exam, setExam] = useState<ExamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [previousScore, setPreviousScore] = useState<number | null>(null);
  const [showMobileNav, setShowMobileNav] = useState(false);

  useEffect(() => {
    const fetchExam = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const res = await certificateExamApi.getCertificateExamForStudent(id);
        if (res.success && res.data) {
          setExam(res.data);
          try {
            const courseRes = await coursesApi.getCourse(id);
            if (courseRes.success && courseRes.data) {
              setCourse(courseRes.data);
            }
          } catch (courseErr) {
            console.error("Failed to load course:", courseErr);
          }
        }
      } catch (err) {
        console.error("Failed to load exam:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchExam();
  }, [id]);

  useEffect(() => {
    if (!timeLeft || submitted || loading) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timer = setInterval(
      () => setTimeLeft((prev) => (prev || 0) - 1),
      1000,
    );
    return () => clearInterval(timer);
  }, [timeLeft, submitted, loading]);

  useEffect(() => {
    if (exam && !timeLeft) {
      const examDurationInSeconds = exam.examDuration * 60;
      setTimeLeft(examDurationInSeconds);
    }
  }, [exam, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSelectAnswer = (questionId: string, optionId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = async () => {
    if (!exam || !id) return;

    setSubmitting(true);
    try {
      const res = await certificateExamApi.submitCertificateExam(exam._id, {
        answers,
        timeSpent: exam.examDuration * 60 - (timeLeft || 0),
      });

      if (res.success) {
        setScore(res.data.score);
        setPassed(res.data.passed);
        setSubmitted(true);

        if (res.data.passed) {
          showSuccess(
            `🎉 Congratulations! You passed the exam with ${Math.round(res.data.score)}%!`,
          );
          setTimeout(() => navigate(`/course/${id}/finish`), 3000);
        } else {
          showError(
            `You scored ${Math.round(res.data.score)}%. ${exam.passingScore}% required to pass.`,
          );
        }
      }
    } catch (err: any) {
      showError(err.message || "Failed to submit exam");
    } finally {
      setSubmitting(false);
    }
  };

  const currentQ = exam?.questions[currentQuestion];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="px-4 text-center">
          <div className="relative">
            <Target className="w-10 h-10 mx-auto mb-3 text-orange-500 animate-pulse" />
            <div className="absolute inset-0 rounded-full bg-orange-500/20 animate-ping" />
          </div>
          <p className="text-xs text-gray-500 animate-pulse">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!exam || !currentQ) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="mb-1 text-sm text-gray-600">Exam not available</p>
          <Link to="/" className="text-xs text-orange-600 hover:underline">
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  const totalQuestions = exam.questions.length;

  return (
    <div className="min-h-screen pb-20 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      {/* Mobile Header */}
      <div className="sticky top-0 z-20 border-b border-gray-200 shadow-sm bg-white/95 backdrop-blur-sm">
        <div className="px-3 py-2.5 md:px-4 md:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <Link
                to={`/course/${id}`}
                className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <div>
                <h1 className="text-xs font-semibold text-gray-900 line-clamp-1 max-w-[120px] sm:max-w-none md:text-sm">
                  {exam.title}
                </h1>
                <p className="text-[10px] text-gray-500">
                  Q{currentQuestion + 1} of {totalQuestions}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {timeLeft !== null && !submitted && (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg font-mono text-[10px] md:text-xs ${
                    timeLeft < 60
                      ? "bg-red-100 text-red-600"
                      : "bg-orange-50 text-orange-600"
                  }`}
                >
                  <Clock className="w-3 h-3" />
                  <span>{formatTime(timeLeft)}</span>
                </div>
              )}
              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileNav(!showMobileNav)}
                className="lg:hidden p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              >
                {showMobileNav ? (
                  <X className="w-4 h-4" />
                ) : (
                  <Menu className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {showMobileNav && (
        <div className="lg:hidden fixed inset-x-0 top-[49px] md:top-[57px] z-30 bg-white border-b border-gray-200 shadow-lg p-3">
          <div className="space-y-2">
            <Link
              to="/dashboard"
              className="flex items-center gap-2.5 p-2.5 text-xs text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              onClick={() => setShowMobileNav(false)}
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>Dashboard</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2.5 p-2.5 text-xs text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              onClick={() => setShowMobileNav(false)}
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </Link>
            <div className="pt-2 border-t border-gray-100">
              <div className="px-2.5 py-1.5 text-[10px] text-gray-500">
                Attempt {Math.min(attempts + 1, exam.attemptsAllowed || 3)}/
                {exam.attemptsAllowed || 3}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="w-full h-1 bg-gray-200">
        <div
          className="h-full transition-all duration-300 bg-gradient-to-r from-orange-500 to-orange-600"
          style={{
            width: `${((currentQuestion + 1) / totalQuestions) * 100}%`,
          }}
        />
      </div>

      {/* Main Content */}
      <div className="max-w-3xl px-3 py-4 mx-auto md:px-4 md:py-6">
        {submitted ? (
          <div className="space-y-4 md:space-y-5">
            {/* Result Card */}
            <div
              className={`p-5 rounded-xl border text-center md:p-6 ${
                passed
                  ? "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200"
                  : "bg-gradient-to-br from-red-50 to-rose-50 border-red-200"
              }`}
            >
              <div
                className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3 md:w-16 md:h-16 ${
                  passed ? "bg-emerald-100" : "bg-red-100"
                }`}
              >
                {passed ? (
                  <Trophy className="w-7 h-7 text-emerald-600 md:w-8 md:h-8" />
                ) : (
                  <XCircle className="text-red-600 w-7 h-7 md:w-8 md:h-8" />
                )}
              </div>
              <h2
                className={`text-base font-bold mb-1 md:text-lg ${passed ? "text-emerald-700" : "text-red-700"}`}
              >
                {passed ? "Exam Passed! 🎉" : "Exam Not Passed"}
              </h2>
              <p className="mb-1 text-sm text-gray-600 md:text-base">
                Your Score: <span className="font-bold">{Math.round(score)}%</span>
              </p>
              <p className="text-[11px] text-gray-500 mb-4 md:text-xs">
                Passing Score: {exam.passingScore}%
              </p>

              {passed ? (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-emerald-600 md:text-sm">
                    Your certificate is now available!
                  </p>
                  <Link
                    to={`/course/${id}/finish`}
                    className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg hover:shadow-md transition-all"
                  >
                    <Award className="w-3.5 h-3.5" />
                    View Certificate
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-red-600 md:text-sm">
                    You need {exam.passingScore}% to pass. Try again.
                  </p>
                  {attempts < (exam.attemptsAllowed || 3) - 1 && (
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setAnswers({});
                        setCurrentQuestion(0);
                        setTimeLeft(exam.examDuration * 60);
                      }}
                      className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg hover:shadow-md transition-all"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Retake Exam
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Review Answers */}
            <div className="space-y-3 md:space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 md:text-base">
                Review Your Answers
              </h3>
              {exam.questions.map((q, idx) => {
                const userAnswer = answers[q.id];
                
                return (
                  <div
                    key={q.id}
                    className="p-3 bg-white border border-gray-200 rounded-lg md:p-4"
                  >
                    <div className="mb-2">
                      <p className="text-xs font-medium text-gray-900 md:text-sm">
                        {idx + 1}. {q.questionText}
                      </p>
                      <p className="text-[9px] text-gray-500 mt-0.5 md:text-[10px]">
                        {q.points || 1} point{q.points !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] text-gray-500 md:text-xs">
                        Your answer:{" "}
                        <span className="font-medium text-gray-700">
                          {q.options.find((o) => o.id === userAnswer)
                            ?.optionText ||
                            userAnswer ||
                            "Not answered"}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Back to Course */}
            <div className="flex justify-center pt-3">
              <Link
                to={`/course/${id}`}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-[10px] font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors md:text-xs"
              >
                <Home className="w-3 h-3" />
                Back to Course
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-5 md:space-y-6">
            {/* Question Card */}
            <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-xl md:p-6">
              <div className="flex items-start gap-2.5 md:gap-3">
                <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 text-xs font-semibold text-orange-600 bg-orange-100 rounded-full md:w-7 md:h-7 md:text-sm">
                  {currentQuestion + 1}
                </div>
                <div className="flex-1">
                  <h2 className="text-sm font-medium text-gray-900 mb-0.5 md:text-base">
                    {currentQ.questionText}
                  </h2>
                  <p className="text-[10px] text-gray-500 md:text-xs">
                    {currentQ.points || 1} point
                    {(currentQ.points || 1) !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>

              {/* Options */}
              <div className="mt-4 space-y-2 md:mt-5 md:space-y-2.5">
                {currentQ.options.map((option: any, idx: number) => {
                  const optionId = option.id || String(idx);
                  const optionText = option.optionText || "";
                  const isSelected = answers[currentQ.id] === optionId;

                  return (
                    <button
                      key={optionId}
                      onClick={() => handleSelectAnswer(currentQ.id, optionId)}
                      className={`w-full p-2.5 text-left rounded-lg transition-all duration-200 md:p-3 ${
                        isSelected
                          ? "bg-orange-50 border border-orange-300 text-gray-900"
                          : "bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-center gap-2.5 md:gap-3">
                        <div
                          className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSelected
                              ? "border-orange-500 bg-orange-500"
                              : "border-gray-400"
                          }`}
                        >
                          {isSelected && (
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          )}
                        </div>
                        <span className="flex-1 text-xs font-medium break-words md:text-sm">
                          {optionText}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Navigation Buttons - Fixed for large devices */}
            <div className="flex items-center justify-between gap-3">
              {/* Previous Button */}
              <button
                onClick={() =>
                  setCurrentQuestion((prev) => Math.max(0, prev - 1))
                }
                disabled={currentQuestion === 0}
                className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:text-orange-600 hover:border-orange-300 hover:bg-orange-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed md:px-6 md:py-2.5 md:text-sm"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Previous</span>
              </button>

              {/* Next/Submit Button */}
              {currentQuestion < totalQuestions - 1 ? (
                <button
                  onClick={() =>
                    setCurrentQuestion((prev) =>
                      Math.min(totalQuestions - 1, prev + 1),
                    )
                  }
                  className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg hover:shadow-md transition-all md:px-6 md:py-2.5 md:text-sm"
                >
                  <span>Next</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={
                    submitting || Object.keys(answers).length < totalQuestions
                  }
                  className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed md:px-6 md:py-2.5 md:text-sm"
                >
                  <Trophy className="w-3.5 h-3.5" />
                  {submitting ? "Submitting..." : "Submit Exam"}
                </button>
              )}
            </div>

            {/* Mobile Question Navigation Dots - Scrollable */}
           
          </div>
        )}
      </div>

      {/* Progress Footer */}
      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-2.5 md:p-3">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-medium text-gray-600 md:text-xs">
                Progress: {Object.keys(answers).length}/{totalQuestions}
              </span>
              <div className="flex gap-2">
                <span className="text-[9px] text-emerald-600 md:text-[10px]">
                  {Object.keys(answers).length} answered
                </span>
                <span className="text-[9px] text-red-500 md:text-[10px]">
                  {totalQuestions - Object.keys(answers).length} left
                </span>
              </div>
            </div>
            <div className="w-full h-1 overflow-hidden bg-gray-200 rounded-full">
              <div
                className="h-full transition-all duration-300 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"
                style={{
                  width: `${(Object.keys(answers).length / totalQuestions) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}